'use client';

import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel-pro';
import { placeHolderImage } from '@/constants/placeHolderImage';
import { useIsMobile } from '@/hooks/use-is-mobile';
import * as AspectRatioPrimitive from '@radix-ui/react-aspect-ratio';
import clsx from 'clsx';
import Image from 'next/image';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import NoSsr from '../no-ssr';

const AspectRatio = AspectRatioPrimitive.Root;

interface Props {
  images: any[];
  videoUrl: string;
  activeIndex: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  productName: string;
  seoColorLabel: string;
}

const MainImageCarousel = (props: Props) => {
  const { images, videoUrl, activeIndex, setActiveIndex, productName, seoColorLabel } = props;

  const isYoutubeUrl =
    !!videoUrl &&
    (videoUrl.indexOf('https://youtu.be') === 0 ||
      videoUrl.indexOf('https://youtube.com') === 0 ||
      videoUrl.indexOf('https://www.youtube.com') === 0);

  const reactPlayerRef = useRef<null | ReactPlayer>(null);
  const { isMobile } = useIsMobile();
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [activeDotIndex, setActiveDotIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const [showVideoCover, setShowVideoCover] = useState(true);
  const prevPropsRef = useRef({ activeIndex });
  const isDesktopVideoPlayedOnceRef = useRef(false);
  const isVideoPlayingRef = useRef(false);

  const handleReactPlayerPlay = () => {
    const player = reactPlayerRef.current?.getInternalPlayer();

    if (!player) {
      return;
    }

    if (player instanceof HTMLVideoElement) {
      player.play();
      return;
    }

    if (typeof player === 'object') {
      reactPlayerRef.current?.getInternalPlayer()?.playVideo?.();
    }
  };

  const handleReactPlayerPause = () => {
    const player = reactPlayerRef.current?.getInternalPlayer();

    if (!player) {
      return;
    }

    if (player instanceof HTMLVideoElement) {
      player.pause();
      return;
    }

    if (typeof player === 'object') {
      reactPlayerRef.current?.getInternalPlayer()?.pauseVideo?.();
    }
  };

  if (prevPropsRef.current.activeIndex !== activeIndex) {
    prevPropsRef.current.activeIndex = activeIndex;

    if (isMobile && videoUrl && !showVideoCover) {
      setShowVideoCover(true);
      handleReactPlayerPause();
    }

    if (!isMobile && videoUrl && isVideoPlayingRef.current) {
      handleReactPlayerPause();
      isVideoPlayingRef.current = false;
    }
  }

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    carouselApi?.scrollTo(activeIndex, false);
  }, [activeIndex, carouselApi]);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const handler = api => {
      const curIndex = api.selectedScrollSnap();
      setActiveIndex(curIndex);
    };

    carouselApi.on('select', handler);

    return () => {
      carouselApi.off('select', handler);
    };
  }, [carouselApi, setActiveIndex]);

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!carouselApi) {
        return;
      }
      carouselApi.scrollTo(index);
    },
    [carouselApi],
  );

  const onInit = useCallback((emblaApi: CarouselApi) => {
    if (!emblaApi) {
      return;
    }

    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: CarouselApi) => {
    if (!emblaApi) {
      return;
    }

    setActiveDotIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    onInit(carouselApi);
    onSelect(carouselApi);
    carouselApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);
  }, [carouselApi, onInit, onSelect]);

  const handlePlayVideo = () => {
    setShowVideoCover(false);
    handleReactPlayerPlay();
    isVideoPlayingRef.current = true;

    if (!isMobile) {
      isDesktopVideoPlayedOnceRef.current = true;
    }
  };

  const handlePauseVideo = () => {
    handleReactPlayerPause();
    isVideoPlayingRef.current = false;

    if (isMobile) {
      setShowVideoCover(true);
      return;
    }

    if (!isDesktopVideoPlayedOnceRef.current) {
      setShowVideoCover(true);
    }
  };

  const handleReactPlayerOnPlay = () => {
    isVideoPlayingRef.current = true;
  };

  return (
    <div className='relative flex-1 text-[0] lg_max-w-[720px]'>
      {images.length > 1 ? (
        <>
          <button
            className={clsx(
              'absolute left-0 top-1/2 h-[46px] w-[46px] -translate-y-1/2 items-center justify-center',
              'z-[6] hidden rounded-[10px] border-1 border-solid border-[#adadad]  bg-white p-0 text-[#666] outline-0 lg_flex',
              'lg_hover_bg-primary lg_hover_text-white',
            )}
            type='button'
            onClick={() => {
              carouselApi?.scrollPrev();
            }}
          >
            <svg
              width='10'
              height='16'
              viewBox='0 0 10 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M8.5 1.5L1.5 8L8.5 14.5'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
          <button
            className={clsx(
              'absolute right-0 top-1/2 h-[46px] w-[46px] -translate-y-1/2 items-center justify-center',
              'z-[6] hidden rounded-[10px] border-1 border-solid border-[#adadad]  bg-white p-0 text-[#666] outline-0 lg_flex',
              'lg_hover_bg-primary lg_hover_text-white',
            )}
            type='button'
            onClick={() => {
              carouselApi?.scrollNext();
            }}
          >
            <svg
              width='9'
              height='15'
              viewBox='0 0 9 15'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M1 14L8 7.5L1 1'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        </>
      ) : null}
      <Carousel
        opts={{ loop: true }}
        setApi={setCarouselApi}
        autoPlayOpts={{ playOnInit: false }}
      >
        <CarouselContent className='ml-0 flex w-full lg_max-w-[762px]'>
          {images.reduce((acc, item, index, arr) => {
            const imageAlt = [seoColorLabel, productName, index + 1].filter(Boolean).join(' ');

            acc.push(
              <CarouselItem
                key={index}
                className='flex-shrink-0 flex-grow-0 basis-full pl-0'
              >
                <div className='group relative flex-shrink overflow-hidden lg_hover_bg-no-repeat'>
                  <Image
                    src={item.image}
                    width={1500}
                    height={1500}
                    className='h-auto w-full'
                    alt={imageAlt}
                    title={imageAlt}
                    unoptimized
                    placeholder={placeHolderImage}
                    priority={index === 0}
                  />
                </div>
              </CarouselItem>,
            );

            if (videoUrl && index === 0) {
              acc.push(
                <CarouselItem
                  key={arr.length}
                  className='flex-none basis-full'
                >
                  <AspectRatio ratio={1500 / 1500}>
                    {isYoutubeUrl && !showVideoCover ? (
                      <button
                        type='button'
                        className='absolute right-3 top-[60px] z-10 m-0 flex h-10 w-10 items-center justify-center rounded-full border-none bg-white/30 p-0 outline-none lg_hidden'
                        onClick={handlePauseVideo}
                      >
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='18'
                          height='18'
                          fill='none'
                          viewBox='0 0 18 18'
                        >
                          <path
                            stroke='#fff'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth='1.5'
                            d='M17 17 1 1m16 0L1 17'
                          ></path>
                        </svg>
                      </button>
                    ) : null}

                    {showVideoCover ? (
                      <>
                        <div className='absolute left-0 top-0 z-[7] h-full w-full cursor-pointer overflow-hidden'>
                          <div className='absolute left-0 top-0 h-full w-full backdrop-blur-sm'></div>
                          <Image
                            src={arr[0].image}
                            width={1500}
                            height={1500}
                            className='h-full w-full object-cover'
                            alt={imageAlt}
                            title={imageAlt}
                            onClick={handlePlayVideo}
                            unoptimized
                            placeholder={placeHolderImage}
                            priority={index === 0}
                          />
                        </div>
                        <button
                          type='button'
                          className='border-0 absolute left-1/2 top-1/2 z-[8] -translate-x-1/2 -translate-y-1/2 transform cursor-pointer bg-transparent p-0 outline-none'
                          onClick={handlePlayVideo}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='50'
                            height='50'
                            fill='none'
                            viewBox='0 0 50 50'
                            className='h-auto w-[60px] lg_w-[100px]'
                          >
                            <circle
                              cx='25'
                              cy='25'
                              r='24'
                              stroke='#fff'
                              strokeWidth='2'
                            ></circle>
                            <path
                              fill='#fff'
                              stroke='#fff'
                              d='M33.75 23.701c1 .577 1 2.02 0 2.598l-12 6.928a1.5 1.5 0 0 1-2.25-1.299V18.072a1.5 1.5 0 0 1 2.25-1.3z'
                            ></path>
                          </svg>
                        </button>
                      </>
                    ) : null}

                    <NoSsr>
                      <div className='absolute left-0 top-0 z-[5] h-full w-full'>
                        <ReactPlayer
                          url={videoUrl}
                          loop={false}
                          controls
                          playbackRate={1}
                          width={'100%'}
                          height={'100%'}
                          playsinline
                          ref={reactPlayerRef}
                          onPlay={handleReactPlayerOnPlay}
                        />
                      </div>
                    </NoSsr>
                  </AspectRatio>
                </CarouselItem>,
              );
            }

            return acc;
          }, [])}
        </CarouselContent>
        <div
          className={clsx(
            'mx-auto mt-1 flex min-h-[15px] w-max items-center justify-center gap-x-1 rounded-[50px] bg-[#efefef] px-2.5 lg_hidden',
            scrollSnaps.length === 0 && 'invisible',
          )}
        >
          {scrollSnaps.map((_, i) => {
            const isActive = activeDotIndex === i;

            if (videoUrl && i === 1) {
              return (
                <button
                  type='button'
                  onClick={() => {
                    onDotButtonClick(i);
                  }}
                  key={i}
                >
                  {isActive ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='15'
                      height='15'
                      fill='none'
                      viewBox='0 0 15 15'
                    >
                      <path
                        fill='#2E4500'
                        d='M13.125 7.5A5.626 5.626 0 0 0 7.5 1.875 5.626 5.626 0 0 0 1.875 7.5 5.626 5.626 0 0 0 7.5 13.125 5.626 5.626 0 0 0 13.125 7.5'
                      />
                      <path
                        fill='#fff'
                        d='M6.338 9.798 9.69 7.773a.32.32 0 0 0 0-.545L6.338 5.202a.316.316 0 0 0-.479.273v4.05a.316.316 0 0 0 .479.273'
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='15'
                      fill='none'
                      viewBox='0 0 16 15'
                    >
                      <path
                        stroke='#2E4500'
                        strokeMiterlimit='10'
                        d='M13.625 7.5A5.626 5.626 0 0 0 8 1.875 5.626 5.626 0 0 0 2.375 7.5 5.626 5.626 0 0 0 8 13.125 5.626 5.626 0 0 0 13.625 7.5Z'
                      />
                      <path
                        fill='#2E4500'
                        d='m6.838 9.798 3.353-2.025a.32.32 0 0 0 0-.545L6.838 5.202a.316.316 0 0 0-.479.273v4.05a.316.316 0 0 0 .479.273'
                      />
                    </svg>
                  )}
                </button>
              );
            }

            return (
              <button
                type='button'
                onClick={() => {
                  onDotButtonClick(i);
                }}
                key={i}
                className={clsx(
                  'rounded-[50%] transition-all duration-xl',
                  isActive ? 'h-2 w-2 bg-primary' : 'h-[5px] w-[5px] bg-[#c4c4c4]',
                )}
              />
            );
          })}
        </div>
      </Carousel>
    </div>
  );
};

export default MainImageCarousel;
